import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { collectClientHints } from '../lib/clientHints';
import { getOrCreateDeviceId, takePendingDeviceEvent } from '../lib/deviceId';
import type { PushSubscriptionPayload } from '../lib/push';
import { UPLOAD_FAILED_MESSAGE, uploadErrorMessage } from '../shared/media-rules';
import { AssigneeInfo, DynamicWidgetConfig, Message, VisitorInfo, WidgetConfig, WidgetState } from '../types';

/** Upload recusado; `message` já é o aviso em português para o visitante. */
class WidgetUploadError extends Error {}

// localStorage helpers para persistir dados do visitante
const STORAGE_KEY_PREFIX = 'troia_widget_visitor_';

export function getStoredVisitorData(channelId: string): VisitorInfo | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${channelId}`);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.name && data.email) return data;
    return null;
  } catch {
    return null;
  }
}

function storeVisitorData(channelId: string, info: VisitorInfo): void {
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${channelId}`,
      JSON.stringify({ name: info.name, email: info.email, phone: info.phone })
    );
  } catch {
    // localStorage pode estar indisponível (iframe, modo privado)
  }
}

interface UseSocketReturn {
  state: WidgetState;
  messages: Message[];
  dynamicConfig: DynamicWidgetConfig | null;
  assigneeInfo: AssigneeInfo | null;
  configLoaded: boolean;
  chatReady: boolean;
  sendMessage: (content: string) => void;
  sendMediaMessage: (file: File) => Promise<void>;
  /** Envia a assinatura de Web Push do visitante (exige visitante já registrado). */
  registerPush: (subscription: PushSubscriptionPayload) => Promise<boolean>;
  connect: (visitorInfo: VisitorInfo) => void;
  disconnect: () => void;
  endConversation: () => void;
}

export const useSocket = (config: WidgetConfig): UseSocketReturn => {
  const [state, setState] = useState<WidgetState>('minimized');
  const [messages, setMessages] = useState<Message[]>([]);
  const [dynamicConfig, setDynamicConfig] = useState<DynamicWidgetConfig | null>(null);
  const [assigneeInfo, setAssigneeInfo] = useState<AssigneeInfo | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [chatReady, setChatReady] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const visitorIdRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);

  /**
   * Ensure socket is connected (reuse existing or create new).
   * Does NOT register visitor - just establishes connection.
   */
  const ensureSocket = useCallback((): Socket => {
    // Reusa socket existente — independente do status (connected / connecting / disconnected
    // com auto-reconnect). Socket.IO já lida com reconexão internamente; destruir e recriar
    // aqui causa perda de listeners (incluindo o once('connect', doRegister) do register).
    if (socketRef.current) {
      return socketRef.current;
    }

    const socket = io(`${config.socketUrl}/widget`, {
      auth: {
        channelId: config.channelId,
        token: config.token,
        domain: window.location.hostname,
        isHostedPage: !!config.hostedPage?.enabled,
      },
      transports: ['websocket', 'polling']
    });

    // Setup shared listeners (only once per socket)
    socket.on('disconnect', () => {
      console.log('❌ [Widget] Disconnected from server');
      setState('disconnected');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ [Widget] Connection error:', error);
      setState('disconnected');
      // Still mark config as loaded so widget shows with static fallback
      setConfigLoaded(true);
    });

    // Receive messages from agent
    socket.on('widget:agent_message', (data: any) => {
      console.log('📨 [Widget] Message received from agent:', data);

      const message: Message = {
        id: data.messageId,
        from: 'agent',
        content: data.content,
        messageType: data.messageType || 'text',
        timestamp: new Date(data.timestamp),
        agentName: data.agentName || 'Agente',
        agentAvatar: data.agentAvatar,
        mediaUrl: data.mediaUrl,
        interactiveData: data.interactiveData
      };

      setMessages((prev) => [...prev, message]);
    });

    // Confirmation that message was processed by backend
    socket.on('widget:message_sent', (data: { messageId: string; conversationId?: string; error?: boolean }) => {
      console.log('✅ [Widget] Message confirmed by server:', data.messageId);

      // Atualizar conversationId quando backend retorna (fix: primeira mensagem)
      if (data.conversationId) {
        conversationIdRef.current = data.conversationId;
      }

      // Atualizar status da mensagem
      setMessages(prev => prev.map(msg =>
        msg.id === data.messageId
          ? { ...msg, status: data.error ? 'failed' : 'sent' }
          : msg
      ));
    });

    // Status updates de mensagens (delivered/read)
    socket.on('widget:message_status', (data: { messageId?: string; status: 'delivered' | 'read' }) => {
      setMessages(prev => prev.map(msg => {
        // Se messageId específico, atualiza só essa mensagem
        if (data.messageId) {
          return msg.id === data.messageId ? { ...msg, status: data.status } : msg;
        }
        // Se sem messageId, atualiza todas as mensagens do visitor com status inferior
        if (msg.from === 'visitor' && msg.status && msg.status !== 'read') {
          return { ...msg, status: data.status };
        }
        return msg;
      }));
    });

    // Error from backend
    socket.on('widget:error', (data: { message: string }) => {
      console.error('⚠️ [Widget] Server error:', data.message);
    });

    // Action click from AI agent
    socket.on('widget:action_click', (data: { type: string; target: string }) => {
      console.log('🖱️ [Widget] Action click received:', data);

      const target = data.target.trim().toLowerCase();
      const doc = window.parent?.document || document;
      const widgetRoot = doc.getElementById('troia-widget-root') || doc.getElementById('troia-widget-container');

      // Helper: check if element is inside the widget itself (should be excluded)
      const isInsideWidget = (el: HTMLElement): boolean => {
        return !!(widgetRoot && widgetRoot.contains(el));
      };

      // Helper: find closest clickable ancestor (a, button) for proper SPA navigation
      const findClickableAncestor = (el: HTMLElement): HTMLElement => {
        let current: HTMLElement | null = el;
        while (current) {
          const tag = current.tagName.toLowerCase();
          if (tag === 'a' || tag === 'button') return current;
          current = current.parentElement;
        }
        return el;
      };

      // Helper: get direct text content (excluding deeply nested children)
      const getDirectText = (el: HTMLElement): string => {
        let text = '';
        for (const node of el.childNodes) {
          if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent || '';
          }
        }
        return text.trim().toLowerCase();
      };

      // 1. Tentar por ID exato
      let element: HTMLElement | null = doc.getElementById(data.target.trim());

      // 2. Tentar por data-click attribute
      if (!element) {
        const dataClickEl = doc.querySelector<HTMLElement>(`[data-click="${data.target.trim()}"]`);
        if (dataClickEl && !isInsideWidget(dataClickEl)) {
          element = dataClickEl;
        }
      }

      // 3. Buscar em elementos nativamente clicáveis primeiro (a, button)
      if (!element) {
        const primarySelectors = 'a, button, [role="button"], [role="link"], [role="menuitem"]';
        const candidates = doc.querySelectorAll<HTMLElement>(primarySelectors);

        for (const el of candidates) {
          if (isInsideWidget(el)) continue;

          const directText = getDirectText(el);
          const fullText = (el.textContent || '').trim().toLowerCase();
          const ariaLabel = (el.getAttribute('aria-label') || '').toLowerCase();
          const title = (el.getAttribute('title') || '').toLowerCase();
          const alt = (el.getAttribute('alt') || '').toLowerCase();
          const dataClick = (el.getAttribute('data-click') || '').toLowerCase();

          // Prefer direct text match (avoids matching parent containers with nested text)
          if (
            directText === target ||
            ariaLabel === target ||
            title === target ||
            dataClick === target ||
            fullText === target ||
            alt === target
          ) {
            element = el;
            break;
          }
        }
      }

      // 4. Fallback: buscar em qualquer elemento visível (span, div, li)
      if (!element) {
        const fallbackSelectors = 'span, li, div, label, img';
        const candidates = doc.querySelectorAll<HTMLElement>(fallbackSelectors);

        for (const el of candidates) {
          if (isInsideWidget(el)) continue;

          const directText = getDirectText(el);
          const ariaLabel = (el.getAttribute('aria-label') || '').toLowerCase();
          const title = (el.getAttribute('title') || '').toLowerCase();
          const alt = (el.getAttribute('alt') || '').toLowerCase();
          const dataClick = (el.getAttribute('data-click') || '').toLowerCase();

          if (
            directText === target ||
            ariaLabel === target ||
            title === target ||
            dataClick === target ||
            alt === target
          ) {
            element = el;
            break;
          }
        }
      }

      if (element) {
        const clickTarget = findClickableAncestor(element);
        console.log('🖱️ [Widget] Clicking element:', clickTarget.tagName, clickTarget, '(matched:', element.tagName, element.textContent?.trim(), ')');
        clickTarget.click();
      } else {
        console.warn('🖱️ [Widget] No matching element found for target:', data.target);
      }
    });

    // Action set_input_value from AI agent
    socket.on('widget:action_set_input_value', (data: { type: string; target: string; value?: string }) => {
      console.log('📝 [Widget] Action set_input_value received:', data);

      const target = data.target.trim().toLowerCase();
      const value = data.value || '';
      const doc = window.parent?.document || document;
      const widgetRoot = doc.getElementById('troia-widget-root') || doc.getElementById('troia-widget-container');

      // Helper: check if element is inside the widget itself (should be excluded)
      const isInsideWidget = (el: HTMLElement): boolean => {
        return !!(widgetRoot && widgetRoot.contains(el));
      };

      // Helper: get direct text content (excluding deeply nested children)
      const getDirectText = (el: HTMLElement): string => {
        let text = '';
        for (const node of el.childNodes) {
          if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent || '';
          }
        }
        return text.trim().toLowerCase();
      };

      // 1. Tentar por ID exato
      let element: HTMLElement | null = doc.getElementById(data.target.trim());

      // 2. Tentar por data-click attribute
      if (!element) {
        const dataClickEl = doc.querySelector<HTMLElement>(`[data-click="${data.target.trim()}"]`);
        if (dataClickEl && !isInsideWidget(dataClickEl)) {
          element = dataClickEl;
        }
      }

      // 3. Buscar em inputs, selects e textareas por label, placeholder, name, aria-label, title, adjacent labels
      if (!element) {
        const inputSelectors = 'input, select, textarea';
        const candidates = doc.querySelectorAll<HTMLElement>(inputSelectors);

        for (const el of candidates) {
          if (isInsideWidget(el)) continue;

          const inputEl = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
          const name = (inputEl.name || '').toLowerCase();
          const placeholder = ('placeholder' in inputEl ? (inputEl as HTMLInputElement).placeholder : '').toLowerCase();
          const ariaLabel = (el.getAttribute('aria-label') || '').toLowerCase();
          const title = (el.getAttribute('title') || '').toLowerCase();
          const id = (inputEl.id || '').toLowerCase();

          // Check associated label via for attribute
          let labelText = '';
          if (inputEl.id) {
            const label = doc.querySelector<HTMLLabelElement>(`label[for="${inputEl.id}"]`);
            if (label) {
              labelText = (label.textContent || '').trim().toLowerCase();
            }
          }

          // Check wrapper label (label > input pattern)
          let wrapperLabelText = '';
          const parentLabel = el.closest('label');
          if (parentLabel) {
            wrapperLabelText = getDirectText(parentLabel);
          }

          // Check adjacent sibling label (label + input or label followed by div > input)
          let adjacentLabelText = '';
          const prevSibling = el.previousElementSibling || el.parentElement?.previousElementSibling;
          if (prevSibling && (prevSibling.tagName.toLowerCase() === 'label' || prevSibling.tagName.toLowerCase() === 'span')) {
            adjacentLabelText = (prevSibling.textContent || '').trim().toLowerCase();
          }

          if (
            name === target ||
            placeholder === target ||
            ariaLabel === target ||
            title === target ||
            id === target ||
            labelText === target ||
            wrapperLabelText === target ||
            adjacentLabelText === target
          ) {
            element = el;
            break;
          }
        }
      }

      // 4. Fallback: buscar em qualquer elemento visível (span, div, label) que possa conter input filho
      if (!element) {
        const fallbackSelectors = 'span, li, div, label, img';
        const candidates = doc.querySelectorAll<HTMLElement>(fallbackSelectors);

        for (const el of candidates) {
          if (isInsideWidget(el)) continue;

          const directText = getDirectText(el);
          const ariaLabel = (el.getAttribute('aria-label') || '').toLowerCase();
          const title = (el.getAttribute('title') || '').toLowerCase();
          const alt = (el.getAttribute('alt') || '').toLowerCase();
          const dataClick = (el.getAttribute('data-click') || '').toLowerCase();

          if (
            directText === target ||
            ariaLabel === target ||
            title === target ||
            dataClick === target ||
            alt === target
          ) {
            // Try to find input/select/textarea child
            const childInput = el.querySelector('input, select, textarea');
            if (childInput) {
              element = childInput as HTMLElement;
            } else {
              element = el;
            }
            break;
          }
        }
      }

      if (element) {
        const tag = element.tagName.toLowerCase();

        if (tag === 'select') {
          // Selecionar option com value ou text correspondente
          const selectEl = element as HTMLSelectElement;
          const option = Array.from(selectEl.options).find(opt =>
            opt.value.toLowerCase() === value.toLowerCase() ||
            opt.text.toLowerCase() === value.toLowerCase()
          );
          if (option) {
            selectEl.value = option.value;
            selectEl.dispatchEvent(new Event('change', { bubbles: true }));
            console.log('📝 [Widget] Selected option:', option.text, 'in', selectEl.name || selectEl.id);
          } else {
            console.warn('📝 [Widget] No matching option found for value:', value);
          }
        } else if (tag === 'input' || tag === 'textarea') {
          // Definir value usando nativeInputValueSetter para compatibilidade com React
          const inputEl = element as HTMLInputElement | HTMLTextAreaElement;
          const proto = tag === 'textarea'
            ? window.HTMLTextAreaElement.prototype
            : window.HTMLInputElement.prototype;
          const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

          if (nativeSetter) {
            nativeSetter.call(inputEl, value);
          } else {
            inputEl.value = value;
          }

          inputEl.dispatchEvent(new Event('input', { bubbles: true }));
          inputEl.dispatchEvent(new Event('change', { bubbles: true }));
          console.log('📝 [Widget] Set value:', value, 'in', inputEl.name || inputEl.id || tag);
        } else {
          // Fallback: tentar encontrar input/select filho
          const childInput = element.querySelector('input, select, textarea') as HTMLElement | null;
          if (childInput) {
            const childTag = childInput.tagName.toLowerCase();

            if (childTag === 'select') {
              const selectEl = childInput as HTMLSelectElement;
              const option = Array.from(selectEl.options).find(opt =>
                opt.value.toLowerCase() === value.toLowerCase() ||
                opt.text.toLowerCase() === value.toLowerCase()
              );
              if (option) {
                selectEl.value = option.value;
                selectEl.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else {
              const inputEl = childInput as HTMLInputElement | HTMLTextAreaElement;
              const proto = childTag === 'textarea'
                ? window.HTMLTextAreaElement.prototype
                : window.HTMLInputElement.prototype;
              const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

              if (nativeSetter) {
                nativeSetter.call(inputEl, value);
              } else {
                inputEl.value = value;
              }

              inputEl.dispatchEvent(new Event('input', { bubbles: true }));
              inputEl.dispatchEvent(new Event('change', { bubbles: true }));
            }
            console.log('📝 [Widget] Set value on child input:', childTag, value);
          } else {
            console.warn('📝 [Widget] Element found but is not input/select/textarea:', element.tagName);
          }
        }
      } else {
        console.warn('📝 [Widget] No matching element found for target:', data.target);
      }
    });

    // Action route from AI agent (navigate to a different page)
    socket.on('widget:action_route', (data: { type: string; target: string }) => {
      console.log('🧭 [Widget] Action route received:', data);

      const route = (data.target || '').trim();
      if (!route) {
        console.warn('🧭 [Widget] Route target is empty');
        return;
      }

      try {
        // Use window.parent for the host page (widget runs inside the customer's site)
        const win = window.parent || window;

        // pushState changes the URL without page reload
        win.history.pushState(null, '', route);

        // Dispatch PopStateEvent so React Router DOM (and other SPA routers) re-render
        win.dispatchEvent(new PopStateEvent('popstate'));

        console.log('🧭 [Widget] Navigated to:', route);
      } catch (error) {
        console.error('🧭 [Widget] Failed to navigate:', error);

        // Fallback: if pushState fails (e.g. cross-origin), try location change
        try {
          const win = window.parent || window;
          win.location.href = route;
        } catch (fallbackError) {
          console.error('🧭 [Widget] Fallback navigation also failed:', fallbackError);
        }
      }
    });

    // Typing indicator
    socket.on('widget:agent_typing', () => {
      // TODO: Implement typing indicator
    });

    // Real-time config updates (admin changed settings or assignee changed)
    socket.on('widget:config_update', (data: { channelConfig?: DynamicWidgetConfig; assigneeInfo?: AssigneeInfo | null }) => {
      console.log('📡 [Widget] Config update received:', data);
      if (data.channelConfig) {
        const incoming = data.channelConfig;
        setDynamicConfig(prev => {
          if (!prev) return incoming;
          const merged = { ...prev, ...incoming };
          // Um config_update parcial (ex: mudança de assignee) pode não carregar
          // `features`. Não deixar isso limpar as flags já conhecidas — senão os
          // botões de mídia somem do input no meio da conversa.
          if (incoming.features === undefined) merged.features = prev.features;
          return merged;
        });
      }
      if (data.assigneeInfo !== undefined) {
        setAssigneeInfo(data.assigneeInfo);
      }
    });

    socketRef.current = socket;
    return socket;
  }, [config]);

  /**
   * Auto-fetch channel config on mount.
   * Connects socket and emits widget:get_channel_config to get position, brandColor, etc.
   * Widget FAB only renders after this completes.
   */
  useEffect(() => {
    const socket = ensureSocket();

    const fetchConfig = () => {
      socket.emit('widget:get_channel_config', (response: {
        success: boolean;
        channelConfig?: DynamicWidgetConfig;
        error?: string;
      }) => {
        if (response.success && response.channelConfig) {
          setDynamicConfig(response.channelConfig);
          console.log('📡 [Widget] Channel config loaded on init:', response.channelConfig);
        }
        setConfigLoaded(true);
      });
    };

    if (socket.connected) {
      fetchConfig();
    } else {
      socket.on('connect', () => {
        console.log('✅ [Widget] Socket connected, fetching channel config...');
        fetchConfig();
      });
    }

    // Sem cleanup que disconnecta — em React 18 StrictMode dev, o cleanup roda no
    // unmount simulado e desconecta o socket compartilhado, perdendo listeners.
    // Cleanup real do socket fica em `disconnect()` (chamado explicitamente quando user
    // encerra a conversa) e na navegação completa fora da SPA.
  }, [ensureSocket]);

  /**
   * Register visitor to get contactId as visitorId.
   * Reuses existing socket connection.
   *
   * Flow:
   * 1. Emit widget:register_visitor with visitor data
   * 2. Backend creates/finds contact, returns contactId
   * 3. contactId becomes visitorId for all future communication
   */
  const connect = useCallback((visitorInfo: VisitorInfo) => {
    console.log('🔌 [Widget] connect() called with:', visitorInfo);
    const socket = ensureSocket();
    console.log('🔌 [Widget] socket state — connected:', socket.connected, 'visitorIdRef:', visitorIdRef.current);

    if (visitorIdRef.current && socket.connected) {
      setState('connected');
      setChatReady(true);
      return;
    }

    setState('connecting');
    setChatReady(false);

    const doRegister = () => {
      console.log('📤 [Widget] Emitting widget:register_visitor');
      socket.emit('widget:register_visitor', {
        visitorName: visitorInfo.name,
        visitorEmail: visitorInfo.email,
        visitorPhone: visitorInfo.phone || undefined,
        // Injetados via window.troiaWidgetConfig (não vêm do form): aplicam tags e
        // criam/associam customer no registro do contato.
        ...(config.visitorTags?.length ? { visitorTags: config.visitorTags } : {}),
        ...(config.company ? { company: config.company } : {})
      }, (response: { success: boolean; visitorId?: string; conversationId?: string; channelConfig?: DynamicWidgetConfig; assigneeInfo?: AssigneeInfo | null; error?: string }) => {
        console.log('📥 [Widget] register_visitor response:', response);
        if (response.success && response.visitorId) {
          visitorIdRef.current = response.visitorId;
          conversationIdRef.current = response.conversationId || null;
          storeVisitorData(config.channelId, visitorInfo);
          setState('connected');
          console.log(`✅ [Widget] Visitor registered, contactId=${response.visitorId}`);

          // Dispositivo só é registrado em EVENTO (ex.: PWA instalado). Se o
          // evento aconteceu antes de existir contato, ficou pendente no storage.
          const pendingDeviceEvent = takePendingDeviceEvent(config.channelId);
          if (pendingDeviceEvent) {
            // Client Hints trazem o modelo real do aparelho (o User-Agent do
            // Chrome no Android só diz "K"). Assíncrono e opcional: se o
            // browser não suportar, o servidor usa só o User-Agent.
            void collectClientHints().then((clientHints) => {
              socket.emit('widget:register_device', {
                deviceId: getOrCreateDeviceId(),
                event: pendingDeviceEvent,
                ...(clientHints ? { clientHints } : {}),
              });
            });
          }

          // Apply dynamic config from server (may have assignee info now)
          if (response.channelConfig) {
            setDynamicConfig(response.channelConfig);
          }
          if (response.assigneeInfo) {
            setAssigneeInfo(response.assigneeInfo);
          }

          // Se existe conversa ativa, buscar histórico de mensagens
          if (response.conversationId) {
            conversationIdRef.current = response.conversationId;
            console.log(`📜 [Widget] Active conversation found: ${response.conversationId}, fetching history...`);
            socket.emit('widget:get_history',
              { conversationId: response.conversationId, limit: 50 },
              (historyResponse: { success: boolean; messages?: Array<{ id: string; from: 'visitor' | 'agent'; content: string; messageType: string; timestamp: string; agentName?: string; mediaUrl?: string; status?: string; interactiveData?: any }>; error?: string }) => {
                if (historyResponse.success && historyResponse.messages?.length) {
                  const historyMessages: Message[] = historyResponse.messages.map((msg) => ({
                    id: msg.id,
                    from: msg.from,
                    content: msg.content,
                    messageType: (msg.messageType || 'text') as Message['messageType'],
                    timestamp: new Date(msg.timestamp),
                    agentName: msg.agentName,
                    mediaUrl: msg.mediaUrl,
                    status: msg.from === 'visitor' ? (msg.status as Message['status'] || 'sent') : undefined,
                    interactiveData: msg.interactiveData
                  }));
                  setMessages(historyMessages);
                  console.log(`📜 [Widget] Loaded ${historyMessages.length} history messages`);
                }
                // Chat ready after history fetch completes
                setChatReady(true);
              }
            );
          } else {
            // No active conversation, chat is ready immediately
            setChatReady(true);
          }
        } else {
          console.error('❌ [Widget] Failed to register visitor:', response.error);
          setState('disconnected');
          setChatReady(true);
        }
      });
    };

    if (socket.connected) {
      doRegister();
    } else {
      socket.once('connect', doRegister);
    }
  }, [config, ensureSocket]);

  // Disconnect from Socket.IO
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      visitorIdRef.current = null;
      setState('minimized');
    }
  }, []);

  // End conversation (close on backend + reset local state)
  const endConversation = useCallback(() => {
    if (!socketRef.current?.connected || !conversationIdRef.current) return;

    const convId = conversationIdRef.current;

    // Reset local state immediately (don't wait for callback)
    conversationIdRef.current = null;
    setMessages([]);

    socketRef.current.emit('widget:end_conversation',
      { conversationId: convId },
      (response: { success: boolean; error?: string }) => {
        if (response.success) {
          console.log('✅ [Widget] Conversation ended successfully');
        } else {
          console.error('❌ [Widget] Failed to end conversation:', response.error);
        }
      }
    );
  }, []);

  // Send message to agent
  const sendMessage = useCallback((content: string) => {
    if (!socketRef.current?.connected) {
      console.error('❌ [Widget] Socket not connected');
      return;
    }

    if (!visitorIdRef.current) {
      console.error('❌ [Widget] Visitor not registered');
      return;
    }

    const messageId = `msg_${Date.now()}`;

    // Emit message to server (matching backend event name)
    socketRef.current.emit('widget:message', {
      messageId,
      visitorId: visitorIdRef.current,
      message: content,
      messageType: 'text',
      timestamp: new Date().toISOString(),
      metadata: {
        pageUrl: window.location.href,
        userAgent: navigator.userAgent
      }
    });

    // Add to local messages immediately (optimistic update)
    const message: Message = {
      id: messageId,
      from: 'visitor',
      content,
      messageType: 'text',
      timestamp: new Date(),
      status: 'sending'
    };

    setMessages((prev) => [...prev, message]);
  }, []);

  /**
   * Faz upload do arquivo via HTTP e emite widget:message com mediaUrl.
   * Backend retorna o messageType correto baseado no mime type.
   */
  const sendMediaMessage = useCallback(async (file: File): Promise<void> => {
    if (!socketRef.current?.connected) {
      console.error('❌ [Widget] Socket not connected');
      return;
    }
    if (!visitorIdRef.current) {
      console.error('❌ [Widget] Visitor not registered');
      return;
    }

    const apiUrl = config.apiUrl || window.location.origin;
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log('📤 [Widget] Uploading media:', file.name, file.type, file.size);
      const res = await fetch(`${apiUrl}/api/widget/upload/${config.channelId}`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('❌ [Widget] Upload failed:', res.status, errText);
        throw new WidgetUploadError(uploadErrorMessage(res.status, errText));
      }

      const json = (await res.json()) as {
        success: boolean;
        data?: {
          url: string;
          mimeType: string;
          originalName: string;
          size: number;
          messageType: 'image' | 'audio' | 'document' | 'video';
        };
      };

      if (!json.success || !json.data) {
        console.error('❌ [Widget] Upload response invalid');
        return;
      }

      const { url, messageType, originalName } = json.data;
      const messageId = `msg_${Date.now()}`;

      // O filename só faz sentido como texto em documento (é a identidade do arquivo).
      // Para imagem/vídeo/áudio o originalName é ruído e não deve virar legenda no balão.
      const caption = messageType === 'document' ? originalName : '';

      console.log('📤 [Widget] Emitting widget:message with media:', messageType, url);
      socketRef.current.emit('widget:message', {
        messageId,
        visitorId: visitorIdRef.current,
        message: caption,
        messageType,
        mediaUrl: url,
        timestamp: new Date().toISOString(),
        metadata: {
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
        },
      });

      // Optimistic update — mesmo padrão do sendMessage
      setMessages((prev) => [
        ...prev,
        {
          id: messageId,
          from: 'visitor' as const,
          content: caption,
          messageType,
          mediaUrl: url,
          timestamp: new Date(),
          status: 'sending' as const,
        },
      ]);
    } catch (err) {
      console.error('❌ [Widget] Upload error:', err);
      // Quem chama mostra o aviso ao visitante; falha de rede vira o aviso genérico.
      throw err instanceof WidgetUploadError ? err : new WidgetUploadError(UPLOAD_FAILED_MESSAGE);
    }
  }, [config]);

  /**
   * Registra a assinatura de push do visitante.
   *
   * Exige visitante já registrado (a subscription pertence ao contato) — por
   * isso o banner de notificação só aparece com o chat conectado.
   */
  const registerPush = useCallback(async (subscription: PushSubscriptionPayload): Promise<boolean> => {
    const socket = socketRef.current;
    if (!socket || !visitorIdRef.current) return false;

    // Quem permite notificação sem instalar o app não passa pelo
    // `register_device` — então os hints (modelo e versão de SO reais) só
    // chegam ao servidor se forem junto daqui.
    const clientHints = await collectClientHints();

    return new Promise<boolean>((resolve) => {
      socket.emit(
        'widget:register_push',
        { deviceId: getOrCreateDeviceId(), subscription, ...(clientHints ? { clientHints } : {}) },
        (response: { success: boolean; error?: string }) => {
          if (!response.success) {
            console.warn('[Widget] register_push recusado:', response.error);
          }
          resolve(response.success);
        }
      );
    });
  }, []);

  return {
    state,
    messages,
    dynamicConfig,
    assigneeInfo,
    configLoaded,
    chatReady,
    sendMessage,
    sendMediaMessage,
    registerPush,
    connect,
    disconnect,
    endConversation
  };
};
